// صوت الإشعار — نغمةٌ قصيرة تُولَّد في المتصفّح (قرار المالك: «محتاج أسمع الإشعار»).
//
// **بلا ملف صوت في الحزمة**: WebAudio يولّد النغمة من أرقام — فلا ميغابايت تُحمَّل ولا
// أصل ثنائي في المستودع، ونغمةٌ واحدة على كل جهاز.
//
// والمتصفّحات تمنع الصوت قبل أول تفاعل من المستخدم (سياسة التشغيل التلقائي)، فالسياق
// يُهيَّأ عند أول ضغطة ويُستأنف عند كل نغمة — وإن مُنع فلا شيء يُكسر: الجرس والشارة كما هما.
//
// والاختيار للمستخدم ويبقى في جهازه (localStorage) لا في الخادم: تفضيلُ جهازٍ لا بيان عمل.
//
// **وخمس نغمات يختار منها ويسمعها** (قرار المالك بعد أن غُيّرت النغمة مرّتين بالوصف): الصوت
// ذوقٌ لا يُوصف بالكلام — فالأذن تحكم، والاختيار يبقى في الجهاز كتفضيل الكتم.

const KEY = 'icontravel.notificationSound';
const TONE_KEY = 'icontravel.notificationTone';

type Ctx = AudioContext | null;
let ctx: Ctx = null;

/**
 * جزءٌ من نغمة: [التردّد، لحظة بدئه، أعلى شدّة، طول ذيله، (تردّدٌ ينزلق إليه)، (شكل الموجة)] — والتراكب يصنع الجرس،
 * والانزلاق يصنع «البوب» الحديث كإشعارات تطبيقات المراسلة (01/10/2026).
 */
type Part = readonly [freq: number, delay: number, peak: number, tail: number, glideTo?: number, wave?: OscillatorType];

/** `luxe`: من الخمس الفاخرة بطابع آيفون — تُعرض في قسمها (01/10/2026). */
export interface Tone { id: string; ar: string; note: string; luxe?: true; parts: readonly Part[] }

/** النغمات المتاحة — كلٌّ بطابعٍ مختلف بيّن، لا درجاتٍ من شيءٍ واحد. */
export const TONES: readonly Tone[] = [
  /**
   * **نغماتٌ حديثة مميّزة بدل التقليدية** (قرار المالك 01/10/2026: «محتاج أغيّر النغمات دي، تقليدية جدًا، عاوز حاجة مميّزة
   * زي الإشعار بتاع شات الفريق»): بدل نبراتٍ ثابتة كالأجراس القديمة — «بوب» ينزلق صعودًا وفقاعاتٌ ولمعات كإشعارات تطبيقات
   * المراسلة، قصيرةٌ وواضحة. والمعرّفات جديدة كلّها، فمن اختار نغمةً قديمة يرجع للافتراضية الجديدة وحده.
   */
  {
    id: 'pop', ar: 'بوب', note: 'نقرتان تنزلقان لفوق — زي إشعار تطبيقات الشات',
    parts: [[520, 0, 0.9, 0.10, 1250], [1250, 0.06, 0.12, 0.06, undefined, 'triangle'],
            [700, 0.13, 0.95, 0.14, 1750], [1750, 0.20, 0.14, 0.10, undefined, 'triangle']],
  },
  {
    id: 'bubble', ar: 'فقاعة', note: 'فقاعتان طريّتان تطلعان لفوق',
    parts: [[380, 0, 0.9, 0.15, 1150], [460, 0.18, 0.9, 0.17, 1400]],
  },
  {
    id: 'spark', ar: 'لمعة', note: 'ثلاث نبرات برّاقة سريعة — تلفت النظر فورًا',
    parts: [[2637, 0, 0.75, 0.12, undefined, 'triangle'], [3136, 0.07, 0.75, 0.12, undefined, 'triangle'],
            [3951, 0.14, 0.8, 0.30, undefined, 'triangle']],
  },
  {
    id: 'icon', ar: 'iCon', note: 'أربع درجات صاعدة سريعة — نغمة البرنامج المميّزة',
    parts: [[1046.5, 0, 0.8, 0.12, undefined, 'triangle'], [1318.5, 0.08, 0.8, 0.12, undefined, 'triangle'],
            [1568, 0.16, 0.85, 0.12, undefined, 'triangle'], [2093, 0.24, 0.9, 0.42, undefined, 'triangle']],
  },
  {
    id: 'ping', ar: 'بينج', note: 'رنّة واحدة صافية ثم صداها',
    parts: [[1760, 0, 0.95, 0.5, 1720], [3520, 0, 0.18, 0.22], [1760, 0.24, 0.55, 0.45, 1720]],
  },
  /**
   * **خمس عشرة نغمة بريميوم** (قرار المالك 01/10/2026: «عاوز 15 نغمة مختلفة ومميّزة بريميوم، وعاوز تحكّم في درجة وعلوّ
   * الصوت»): كلٌّ بطابعٍ مختلف بيّن — آلات (ماريمبا، كاليمبا، هارب، كريستال)، وحركة (سووش، قطرة ماء، ترِل)، ونغماتٌ
   * رقمية (ديجيتال، نبض، نجاح، أوربت) — لا درجاتٍ من شيءٍ واحد.
   */
  {
    id: 'marimba', ar: 'ماريمبا', note: 'خشبٌ دافئ — ثلاث نقرات متتالية',
    parts: [[784, 0, 0.9, 0.22], [3136, 0, 0.12, 0.06], [1046.5, 0.1, 0.9, 0.22], [4186, 0.1, 0.1, 0.05],
            [1318.5, 0.2, 0.95, 0.36], [5274, 0.2, 0.1, 0.06]],
  },
  {
    id: 'crystal', ar: 'كريستال', note: 'رنين زجاج صافٍ يلمع ويطول',
    parts: [[2093, 0, 0.7, 0.9], [2099, 0, 0.45, 0.9], [4186, 0.02, 0.15, 0.5], [3136, 0.14, 0.5, 0.8]],
  },
  {
    id: 'harp', ar: 'هارب', note: 'خمس أوتار صاعدة بسرعة — فخمة',
    parts: [[1046.5, 0, 0.7, 0.3, undefined, 'triangle'], [1318.5, 0.05, 0.7, 0.3, undefined, 'triangle'],
            [1568, 0.1, 0.75, 0.3, undefined, 'triangle'], [1975.5, 0.15, 0.75, 0.3, undefined, 'triangle'],
            [2093, 0.2, 0.85, 0.6, undefined, 'triangle']],
  },
  {
    id: 'kalimba', ar: 'كاليمبا', note: 'معدنٌ ناعم — نبرتان بطابعٍ أفريقي مميّز',
    parts: [[1174.7, 0, 0.9, 0.45], [6343, 0, 0.12, 0.08], [1480, 0.16, 0.9, 0.55], [7992, 0.16, 0.1, 0.08]],
  },
  {
    id: 'swoosh', ar: 'سووش', note: 'انزلاقٌ سريع لفوق ثم رنّة — زي رسالة طايرة',
    parts: [[300, 0, 0.75, 0.28, 2400], [2400, 0.28, 0.9, 0.4], [4800, 0.28, 0.12, 0.18]],
  },
  {
    id: 'waterdrop', ar: 'قطرة ماء', note: 'قطرتان تنزلان — ناعمة ومميّزة',
    parts: [[1800, 0, 0.9, 0.12, 600], [2100, 0.18, 0.9, 0.14, 700]],
  },
  {
    id: 'trill', ar: 'ترِل', note: 'زقزقة سريعة بين نبرتين',
    parts: [[1568, 0, 0.8, 0.07], [1760, 0.07, 0.8, 0.07], [1568, 0.14, 0.8, 0.07], [1760, 0.21, 0.85, 0.07],
            [2093, 0.28, 0.9, 0.3]],
  },
  {
    id: 'digital', ar: 'ديجيتال', note: 'نبرتان رقميتان حادّتان — تلفت في الزحمة',
    parts: [[988, 0, 0.45, 0.1, undefined, 'square'], [1318.5, 0.12, 0.45, 0.16, undefined, 'square'],
            [1318.5, 0.12, 0.5, 0.2]],
  },
  {
    id: 'success', ar: 'نجاح', note: 'كورد صاعد ثم نبرة عالية — إحساس «تمّ»',
    parts: [[1046.5, 0, 0.6, 0.3, undefined, 'triangle'], [1318.5, 0.04, 0.6, 0.3, undefined, 'triangle'],
            [1568, 0.08, 0.6, 0.3, undefined, 'triangle'], [2093, 0.2, 0.9, 0.5]],
  },
  {
    id: 'orbit', ar: 'أوربت', note: 'نبضتان تدوران لفوق ولتحت — عصرية',
    parts: [[660, 0, 0.85, 0.16, 990, 'triangle'], [990, 0.18, 0.85, 0.2, 660, 'triangle'], [1980, 0.18, 0.12, 0.1]],
  },
  /**
   * **خمسٌ فاخرة بطابع آيفون** (قرار المالك 01/10/2026: «عاوز نغمات متاخدة من Apple أكتر، وتكون high end ولاكشري،
   * زوّد عليه 5 كمان»): **ألحانٌ أصلية لا نسخٌ** — نغمات Apple نفسها محفوظة الحقوق — لكن بنفس الصنعة: موجاتٌ جيبيّة
   * نقيّة، وتوافقيّات جرسٍ غير صحيحة (×2.76، ×5.4) تعطي لمعان الزجاج والمعدن، وأزواجٌ منحرفة بضع هرتزات تصنع «اللمعة»
   * المتموّجة (chorus)، وصدى خافت بعد النغمة كقاعةٍ صغيرة — بدل نقراتٍ جافّة. وتُعرض في الدرج قسمًا مستقلًّا.
   */
  {
    id: 'pearl', ar: 'لؤلؤة', luxe: true, note: 'جرسا زجاج صافيان وصداهما — ناعمة وراقية',
    parts: [[1318.5, 0, 0.8, 0.7], [2637, 0, 0.2, 0.35], [3639, 0, 0.1, 0.18], [7120, 0, 0.04, 0.08],
            [1975.5, 0.16, 0.85, 0.9], [3951, 0.16, 0.2, 0.4], [5452, 0.16, 0.09, 0.2],
            [1975.5, 0.5, 0.22, 0.6]],
  },
  {
    id: 'silk', ar: 'حرير', luxe: true, note: 'كورد فخم يتفتّح نبرةً بعد نبرة — دافئ وطويل',
    parts: [[523.25, 0, 0.35, 1.0], [1046.5, 0.0, 0.6, 0.95], [1318.5, 0.05, 0.6, 0.95], [1568, 0.1, 0.6, 0.95],
            [1975.5, 0.15, 0.7, 1.1], [1979, 0.15, 0.35, 1.1]],
  },
  {
    id: 'golden', ar: 'ذهبي', luxe: true, note: 'نبرتان بلمعة متموّجة — جرسٌ ذهبي',
    parts: [[880, 0, 0.75, 0.75], [883, 0, 0.45, 0.75], [1760, 0, 0.15, 0.35], [2429, 0, 0.08, 0.2],
            [1318.5, 0.2, 0.8, 1.0], [1322.5, 0.2, 0.45, 1.0], [2637, 0.2, 0.15, 0.45], [3639, 0.2, 0.07, 0.25]],
  },
  {
    id: 'sapphire', ar: 'سفير', luxe: true, note: 'ثلاث نقرات زجاجية صاعدة — أنيقة وواضحة',
    parts: [[1174.7, 0, 0.8, 0.35], [4699, 0, 0.1, 0.05], [1568, 0.11, 0.8, 0.4], [6272, 0.11, 0.1, 0.05],
            [2349.3, 0.22, 0.85, 0.8], [2353, 0.22, 0.35, 0.8], [9397, 0.22, 0.06, 0.05],
            [2349.3, 0.55, 0.2, 0.5]],
  },
  {
    id: 'velvet', ar: 'مخمل', luxe: true, note: 'نبرتان دافئتان منخفضتان بصدى — هادئة وفخمة',
    parts: [[698.5, 0, 0.85, 0.6, undefined, 'triangle'], [1397, 0, 0.25, 0.35], [2095.5, 0, 0.08, 0.2],
            [880, 0.18, 0.9, 0.85, undefined, 'triangle'], [1760, 0.18, 0.25, 0.45], [2640, 0.18, 0.08, 0.25],
            [880, 0.55, 0.25, 0.6, undefined, 'triangle']],
  },
]

const DEFAULT_TONE = TONES[0];   // «بوب» (01/10/2026) — بدل النغمات التقليدية

/** هل الصوت مفعَّل على هذا الجهاز؟ الافتراضي: نعم. */
export function soundEnabled(): boolean {
  try { return localStorage.getItem(KEY) !== 'off'; } catch { return true; }
}

export function setSoundEnabled(on: boolean): void {
  try { localStorage.setItem(KEY, on ? 'on' : 'off'); } catch { /* وضع خاص أو تخزين ممنوع */ }
}

/** النغمة المختارة — وما لا يُعرف اسمه يعود إلى الافتراضية لا إلى الصمت. */
export function currentTone(): Tone {
  try {
    const id = localStorage.getItem(TONE_KEY);
    return TONES.find(t => t.id === id) ?? DEFAULT_TONE;
  } catch { return DEFAULT_TONE; }
}

export function setTone(id: string): void {
  try { localStorage.setItem(TONE_KEY, id); } catch { /* تخزين ممنوع — تبقى الافتراضية */ }
}

/**
 * **التحكّم في علوّ الصوت ودرجته** (قرار المالك 01/10/2026: «عاوز تحكّم في درجة وعلوّ الصوت»): لكل جهاز —
 * العلوّ ‎10–100٪‏ (الافتراضي 100)، والدرجة ‎−6…+6‏ أنصاف تون (أعمق ↔ أحدّ، الافتراضي 0). تفضيلٌ في الجهاز كالنغمة.
 */
const VOLUME_KEY = 'icontravel.notificationVolume';
const PITCH_KEY = 'icontravel.notificationPitch';
const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
export function soundVolume(): number {
  try { const v = Number(localStorage.getItem(VOLUME_KEY)); return Number.isFinite(v) && v > 0 ? clamp(v, 10, 100) : 100; } catch { return 100; }
}
export function setSoundVolume(v: number): void {
  try { localStorage.setItem(VOLUME_KEY, String(clamp(Math.round(v), 10, 100))); } catch { /* تخزين ممنوع */ }
}
export function soundPitch(): number {
  try { const v = Number(localStorage.getItem(PITCH_KEY)); return Number.isFinite(v) ? clamp(Math.round(v), -6, 6) : 0; } catch { return 0; }
}
export function setSoundPitch(v: number): void {
  try { localStorage.setItem(PITCH_KEY, String(clamp(Math.round(v), -6, 6))); } catch { /* تخزين ممنوع */ }
}

function audioCtx(): Ctx {
  if (ctx) return ctx;
  try {
    const C = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    ctx = C ? new C() : null;
  } catch { ctx = null; }
  return ctx;
}

/** يعزف أجزاء نغمةٍ بمغلّفٍ ناعم: هجومٌ ‎12ms‏ بلا طقطقة، ثم تلاشٍ أُسّي. */
function play(parts: readonly Part[]): void {
  const c = audioCtx();
  if (!c) return;
  // سياقٌ معلَّق (بعد سكونٍ طويل أو في تبويبٍ مخفيّ): يُستأنف **ثم** يُعزف — كان يُجدول على ساعةٍ واقفة فلا يُسمع
  if (c.state === 'suspended') { void c.resume().then(() => schedule(c, parts), () => undefined); return; }
  schedule(c, parts);
}
/**
 * **أعلى بلا تشويه** (قرار المالك 01/10/2026: «صوت واطي وضعيف… عاوزه نقي ولكن صوت أعلى»): كانت ذروة النغمة نحو 0.2 من
 * الأقصى فتضيع وسط ما يسمعه الموظف. الآن كل نغمة تُضخَّم (LOUDNESS) ثم تمرّ بضاغطٍ (DynamicsCompressor) قبل السمّاعة —
 * يرفع العلوّ المسموع ويمنع القصّ (clipping) فيبقى الصوت صافيًا ولو تراكبت أجزاؤه.
 */
const LOUDNESS = 2.6;
/**
 * **ثم: «برضو واطي لسه، عاوزين نعلّيه كمان شوية»** (قرار المالك 01/10/2026): الضاغط الأوّل (عتبة −14 dB ونسبة 6) كان يكبس
 * الذروة نفسها ولا شيء يعوّضها بعده — فخرجت النغمة نحو ربع العلوّ الأقصى، أعلى قليلًا فقط من الأصل. الآن سلسلة إتقانٍ
 * كالمعتاد في الصوت: ضاغطٌ (عتبة −30 dB ونسبة 8) يرفع ذيل النغمة إلى قرب رأسها، ثم **تعويضٌ (makeup gain) ×8 ≈ +18 dB**،
 * ثم **محدِّدٌ (limiter) عند −4 dB** ثم هامشٌ أخير ×0.9 — فلا قصّ ولا طقطقة. **مقيسٌ** (OfflineAudioContext، 01/10/2026):
 * الذروة −0.4 dB والمتوسّط (RMS) −8.9 dB، وكانت النغمة الأصلية −25.4 dB والنسخة السابقة −15.6 dB.
 */
const MAKEUP_GAIN = 8;
const SAFETY = 0.9;
let master: { ctx: AudioContext; input: AudioNode; out?: GainNode } | null = null;
function output(c: AudioContext): AudioNode {
  if (master?.ctx === c) return master.input;
  try {
    const comp = c.createDynamicsCompressor();
    comp.threshold.value = -30; comp.knee.value = 10; comp.ratio.value = 8;
    comp.attack.value = 0.003; comp.release.value = 0.25;
    const makeup = c.createGain();
    makeup.gain.value = MAKEUP_GAIN;
    const limiter = c.createDynamicsCompressor();
    limiter.threshold.value = -4; limiter.knee.value = 0; limiter.ratio.value = 20;
    limiter.attack.value = 0.001; limiter.release.value = 0.1;
    const safety = c.createGain();
    safety.gain.value = SAFETY;
    comp.connect(makeup).connect(limiter).connect(safety).connect(c.destination);
    master = { ctx: c, input: comp, out: safety };
    return comp;
  } catch { return c.destination; }
}
function schedule(c: AudioContext, parts: readonly Part[]): void {
  try {
    const at = c.currentTime;
    const out = output(c);
    // العلوّ المختار على الهامش الأخير — بعد المحدِّد فلا يرتدّ الضاغط فيعوّض ما خُفِّض
    const vol = soundVolume() / 100;
    if (master?.out) master.out.gain.value = SAFETY * vol * vol;
    const pitch = Math.pow(2, soundPitch() / 12);
    for (const [freq, delay, peak, tail, glideTo, wave] of parts) {
      const osc = c.createOscillator(), gain = c.createGain();
      osc.type = wave ?? 'sine';
      osc.frequency.value = freq * pitch;
      const t0 = at + delay;
      if (glideTo && osc.frequency.setValueAtTime && osc.frequency.exponentialRampToValueAtTime) {
        osc.frequency.setValueAtTime(freq * pitch, t0);
        osc.frequency.exponentialRampToValueAtTime(glideTo * pitch, t0 + Math.max(0.04, tail * 0.6));
      }
      gain.gain.setValueAtTime(0.0001, t0);
      gain.gain.exponentialRampToValueAtTime(Math.min(0.98, peak * LOUDNESS), t0 + 0.012);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + tail);
      osc.connect(gain).connect(out);
      osc.start(t0);
      osc.stop(t0 + tail + 0.02);
    }
  } catch { /* الصوت ممنوع على هذا الجهاز — لا شيء يُكسر */ }
}

/**
 * نغمة الإشعار بالنغمة المختارة على هذا الجهاز.
 * `force` تتخطّى تفضيل الكتم (زرّ التجربة وحده يستعمله)، و`tone` تعزف نغمةً بعينها للتجربة.
 */
export function playNotificationSound(force = false, tone?: Tone): void {
  if (!force && !soundEnabled()) return;
  play((tone ?? currentTone()).parts);
}

/**
 * **صوت شات الفريق — الأعلى** (قرار المالك 01/10/2026: «عاوز أعلى صوت إشعار شات الفريق»): النغمة المختارة **مرّتين
 * متتاليتين** وكل أجزائها بأقصى شدّة (المحدِّد يمنع القصّ) — فرسالة زميلٍ لا تفوت ولو كان الموظف في شاشةٍ أخرى.
 */
export function playTeamChatSound(force = false): void {
  if (!force && !soundEnabled()) return;
  const parts = currentTone().parts;
  const span = Math.max(...parts.map(([, d, , t]) => d + t)) + 0.12;
  const loud = parts.map(([f, d, pk, t, g, w]) => [f, d, Math.max(pk, Math.min(0.98, pk * 1.4)), t, g, w] as Part);
  play([...loud, ...loud.map(([f, d, pk, t, g, w]) => [f, d + span, pk, t, g, w] as Part)]);
}

/**
 * نقرة التنقّل (قرار المالك: «عاوز صوت أثناء الكليك للزراير الرئيسية»).
 *
 * **نقرةٌ خافتة منخفضة** (‎300Hz‏ ومعها توافقيّتها أخفت منها) لا رنّةً عالية: تُحَسّ كزرٍّ
 * حقيقي يُضغط، ولا تلتبس على الأذن بنغمة الإشعار مهما كانت النغمة المختارة — فهي وحدها في
 * نطاقها المنخفض. وقِصَرٌ شديد (‎45ms‏) وخفوتُ صوت، فلا تصير ضجيجًا مع عشرات النقرات يوميًّا.
 *
 * وتتبع **نفس مفتاح الصوت**: من كتم الصوت كتمه كلّه — لا مفتاحان لشيءٍ واحد.
 */
export function playClickSound(): void {
  if (!soundEnabled()) return;
  play([[300, 0, 0.075, 0.045], [600, 0, 0.018, 0.03]]);
}
